import { makeStyles, styled } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import {
  CALLOUT_BACKGROUND,
  PROMO_CALLOUT_BORDER_RADIUS,
} from 'lib/form-field-styles'
import {
  interactiveActivateControlResetStyles,
  interactiveCardInteractionStyles,
} from 'lib/styles/interactive-card-hover'

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    maxHeight: 'calc(100vh - 64px)',
    overflow: 'hidden',
    '& .MuiDialogTitle-root': {
      flex: '0 0 auto',
    },
    '& .MuiDialogContent-root': {
      flex: '1 1 auto',
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
  },
  dialogTitle: {
    padding: 0,
  },
  hero: {
    position: 'relative',
    minHeight: 200,
    color: theme.palette.common.white,
    backgroundColor: '#00234B',
    borderTopLeftRadius: PROMO_CALLOUT_BORDER_RADIUS,
    borderTopRightRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
  heroContent: {
    position: 'absolute',
    left: theme.spacing(3),
    bottom: theme.spacing(3),
    right: 120,
    zIndex: 1,
  },
  heroMeta: {
    position: 'absolute',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    textAlign: 'right',
    zIndex: 1,
  },
  statusChip: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    color: theme.palette.common.white,
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    height: 22,
    marginBottom: theme.spacing(1),
  },
  heroTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.5),
  },
  heroSubtitle: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    opacity: 0.9,
  },
  heroMetaLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    opacity: 0.85,
    marginBottom: theme.spacing(0.5),
  },
  heroMetaValue: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  actionButtonContainer: {
    position: 'absolute',
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
    display: 'flex',
    gap: theme.spacing(1),
    zIndex: 1,
  },
  actionAvatar: {
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    cursor: 'pointer',
    width: 36,
    height: 36,
  },
  contentRoot: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  detailsCard: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  sectionLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    textTransform: 'uppercase',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
    minWidth: 0,
    width: '100%',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  detailPrimary: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.4,
  },
  detailSecondary: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  detailRowContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    flex: '1 1 auto',
    minWidth: 0,
  },
  detailCopy: {
    minWidth: 0,
    flex: '1 1 auto',
  },
  markAsPaidButton: {
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.75),
    },
  },
  callout: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: CALLOUT_BACKGROUND,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  cancelPaymentRequestButtons: {
    [theme.breakpoints.down('xs')]: {
      marginTop: '1rem',
    },
  },
  cancelCard: {
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
    boxSizing: 'border-box',
    marginBottom: theme.spacing(2),
  },
  notesCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  notesCardClickable: {
    ...interactiveCardInteractionStyles,
    '&:hover': {
      backgroundColor: '#FAFBFC',
      borderColor: '#D0D5D8',
    },
  },
  notesPreviewHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  notesActivateArea: {
    ...interactiveActivateControlResetStyles,
    flex: 1,
    minWidth: 0,
  },
  notesMenuWrap: {
    flexShrink: 0,
    margin: theme.spacing(-1, -1, 0, 0),
  },
  notesMenuButton: {
    cursor: 'pointer',
    minWidth: 0,
    padding: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  notesMenuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  notesTip: {
    marginBottom: theme.spacing(2),
  },
  notesSessionWhen: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
  },
  notesSessionTitle: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  notesLoading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  notesPanel: {
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  notesEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  notesEmptyText: {
    color: theme.palette.text.secondary,
  },
  notesPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  notesPreviewTitle: {
    display: 'block',
    fontSize: '0.9375rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  notesPreviewContent: {
    display: 'block',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'pre-wrap',
  },
  notesPreviewMeta: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(0.5),
  },
  cancelCardHeader: {
    padding: theme.spacing(2.5, 3, 0),
  },
  cancelCardTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: 1.3,
    color: '#172024',
  },
  cancelCardBody: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  cancelCardText: {
    maxWidth: '60%',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.5,
    color: '#6B7C87',
    textAlign: 'left',
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  cancelActionButton: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    width: 'auto',
    minWidth: 'auto',
    maxWidth: 'none',
    height: 'auto',
    minHeight: 36,
    maxHeight: 'none',
    padding: '6px 16px',
    fontWeight: 600,
    flexShrink: 0,
  },
  editSessionRoot: {
    width: '100%',
  },
  editSessionWarning: {
    background: 'rgb(255, 245, 229)',
    borderRadius: '10px',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  editSessionWarningList: {
    marginTop: '4px',
    marginBottom: 0,
  },
  editSessionWarningBody: {
    padding: '8px 4px',
    justifyContent: 'start',
  },
  editSessionHelper: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  paidOfflineIntroSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  paidOfflineDivider: {
    marginBottom: theme.spacing(2),
  },
  editSessionActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(2),
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    boxSizing: 'border-box',
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    '&.MuiIconButton-root': {
      padding: 6,
    },
    '&:hover': {
      backgroundColor: '#F4F6F8',
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
    },
  },
}))

const ActionsWrapper = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexShrink: 0,
  '& svg, & .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
  },
}))

export { useStyles, ActionsWrapper }
